import React from 'react'
import _ from 'lodash/fp'

import Spinner from '@fe/ui/Spinner'

import colors from '@fe/ui/colors'

import { getClubLogo, getClubImageSmall } from '../../config'
import GenericMap, { GlfrInfoWindow } from '../../components/Map'
import { generateBasicMarker, generateCustomerMarker, generateHomeClubMarker } from '../../images'

// Map things
import {
  Marker
} from 'react-google-maps'
import { MarkerClusterer } from 'react-google-maps/lib/components/addons/MarkerClusterer'

const ClubInfoWindow = ({ activeMarkerId, club, onCloseClick, played }) => {
  const visible = React.useMemo(() => {
    return activeMarkerId === _.get('id', club)
  }, [activeMarkerId, club])

  const media = React.useMemo(() => {
    if (!visible) {
      return undefined
    }
    const galleryImage = _.flow(
      _.get('gallery_file_names'),
      _.first
    )(club)
    return galleryImage && `url(${getClubImageSmall(galleryImage)})`
  }, [club, visible])

  return (
    <GlfrInfoWindow
      media={media}
      visible={visible}
      onCloseClick={onCloseClick}
      title={_.get('name', club)}
      left={played && <div dangerouslySetInnerHTML={{ __html: `${played} rounds played` }} />}
    />
  )
}

const ClubMarker = ({ played, club, onClick, children, homeClub, markers }) => {
  const color = React.useMemo(() => {
    if (played) {
      return colors.primaryDarkGreen
    } else {
      return colors.paleGreen
    }
  }, [club, played])

  const [image, setImage] = React.useState(markers[color])

  const isHomeClub = React.useMemo(() => {
    return _.get('id', club) === _.get('id', homeClub)
  }, [homeClub, club])

  const customer = React.useMemo(() => {
    return _.get('customer', club)
  }, [club])

  React.useEffect(() => {
    const logo = getClubLogo(_.get('id', club))

    if (isHomeClub) {
      generateHomeClubMarker(logo, 50, 50, color).then((image) => {
        setImage(image)
      })
    } else if (customer) {
      generateCustomerMarker(logo, 50, 50, color).then((image) => {
        setImage(image)
      })
    }
  }, [])

  const handleMarkerClick = React.useCallback(() => onClick(_.get('id', club)), [club])

  if (!club || (!_.get('position.longitude', club) || !_.get('position.latitude', club))) {
    return false
  }

  // if the image is not succesfully created, then a red standard marker is displayed
  // The zIndex makes sure that paying clubs are always rendered on top of basic clubs
  return (
    <Marker
      zIndex={isHomeClub
        ? 5
        : played
          ? customer
              ? 4
              : 3
          : customer
            ? 2
            : 1}
      position={{
        lat: _.get('position.latitude', club),
        lng: _.get('position.longitude', club)
      }}
      icon={image && { url: image }}
      onClick={handleMarkerClick}
    >
      {children}
    </Marker>
  )
}

const PlayedMap = ({ playedClubs, clubs, classes, homeClub, ...props }) => {
  const [activeMarker, setActiveMarker] = React.useState()
  const [markers, setMarkers] = React.useState({})

  React.useEffect(() => {
    generateBasicMarker(40, 40, colors.paleGreen).then(
      (image) => {
        setMarkers(_.set(colors.paleGreen, image))
      }
    )
    generateBasicMarker(40, 40, colors.primaryDarkGreen).then(
      (image) => {
        setMarkers(_.set(colors.primaryDarkGreen, image))
      }
    )
  }, [])

  const handleMapClick = React.useCallback(() => {
    setActiveMarker(undefined)
  }, [])

  const handleMarkerClick = React.useCallback((id) => {
    setActiveMarker(id)
  })

  const position = React.useMemo(() => {
    if (_.get('position', homeClub)) {
      return {
        lat: _.get('position.latitude', homeClub),
        lng: _.get('position.longitude', homeClub)
      }
    }
    return undefined
  }, [homeClub])

  // cooperative gestureHandling means that you have to ctrl+scroll to zoom and use 2 fingers to move he map on phones etc.
  // mapTypeControl controls whether or not to show the vector<-> sat changer
  if (!_.has(colors.paleGreen, markers) || !_.has(colors.primaryDarkGreen, markers)) {
    return false
  }
  if (_.isEmpty(clubs)) {
    return <Spinner title='Loading map' />
  }
  return (
    <GenericMap
      key={_.get('id', homeClub)}
      options={{ gestureHandling: 'cooperative', mapTypeControl: false }}
      placeholder='Find clubs near...'
      onClick={handleMapClick}
      containerElement={<div style={{ height: 'calc(100vh - 64px)', paddingTop: 80 }} />}
      className={classes.widthCalc}
      defaultZoom={position ? 9 : 5}
      defaultCenter={position || { lat: 55.1, lng: 9.1 }}
    >
      <MarkerClusterer
        averageCenter
        enableRetinaIcons={false}
        minimumClusterSize={3}
        gridSize={60}
      >
        {_.map((club) => (
          <ClubMarker
            played={_.get(_.get('id', club), playedClubs)}
            homeClub={homeClub}
            key={_.get('id', club)}
            club={club}
            markers={markers}
            onClick={(id) => {
              if (activeMarker === id) {
                handleMapClick()
              } else {
                handleMarkerClick(id)
              }
            }}
          >
            <ClubInfoWindow
              club={club}
              played={_.get(_.get('id', club), playedClubs)}
              onCloseClick={handleMapClick}
              activeMarkerId={activeMarker}
            />
          </ClubMarker>
        ), clubs)}
      </MarkerClusterer>
    </GenericMap>
  )
}

export default PlayedMap
